import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ChangeDetectionStrategy,
  OnInit,
  QueryList,
  ViewChildren,
  Inject,
} from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  FormArray,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { untilDestroyed } from '@ngneat/until-destroy';
import { tap, Observable, of, filter } from 'rxjs';
import { v4 as uuid } from 'uuid';
import cloneDeep from 'lodash.clonedeep';

import {
  DprActivityGridData,
  DprActivityCustomDataGridData,
} from '@dpr-details/models/grid';
import { SelectionItem, NumericInputOptions } from '@grid/models';
import { StaticMessage, UtcDateTimeOptions } from '@shared/enums';
import { canDeactivateUnsavedChanges } from '@shared/guards';
import { DataType } from '@shared/models';
import { BaseDialogComponent } from '@shared/models/component';
import {
  ActivityGroup,
  Activity,
  CustomData,
  ActivityMobilisation,
  Location,
} from '@shared/models/project-setup';
import { SyncService } from '@shared/services/grid';
import {
  getCurrentTimeAsString,
  compareTime,
  convertTimeToDate,
  convertTimeToDisplayFormat,
  convertDateToDisplayFormat,
  convertToDateTimeWithoutTimeZoneSuffix,
  Constants,
  toUTCDate,
} from '@shared/utils';
import { DprEditActivityDialogEditComponent } from '../dpr-edit-activity-dialog-edit/dpr-edit-activity-dialog-edit.component';
import { DateTime } from '@dpr-details/models';
import { ToastNotificationService } from '@shared/services';
import { DIALOG_DATE_FORMATS } from '@shared/material/date-picker';
import { MAT_DATE_FORMATS } from '@angular/material/core';

@Component({
  selector: 'app-dpr-activities-dialog-mode',
  templateUrl: './dpr-activities-dialog-mode.component.html',
  styleUrls: ['./dpr-activities-dialog-mode.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: MAT_DATE_FORMATS, useValue: DIALOG_DATE_FORMATS }],
})
export class DprActivitiesDialogModeComponent
  extends BaseDialogComponent
  implements OnInit, AfterViewChecked
{
  @ViewChildren(MatDatepicker)
  dateTimePickers: QueryList<MatDatepicker<any>>;

  selectedDateTimes = new Map<MatDatepicker<any>, DateTime>();

  pendingTimes = new Map<MatDatepicker<any>, string>();

  title: string;
  form: FormGroup;
  activityGroupOptions: ActivityGroup[] = [];
  activityOptions: Activity[] = [];
  locationOptions: Location[] = [];
  startTime: string;
  finishTime: string;
  isActivityMilestone: boolean = false;
  hasActivityChanged: boolean = true;
  minDate: Date = toUTCDate(
    new Date(Constants.DateTime.MinConfigurableDate),
    UtcDateTimeOptions.UtcDateWithoutTime
  );

  StaticMessage = StaticMessage;
  DataType = DataType;
  Constants = Constants;

  isFormValid: boolean = true;

  selectControls = [
    {
      formControlName: 'activityGroup',
      label: 'Activity Group',
      optionsKey: 'activityGroupOptions',
    },
    {
      formControlName: 'activity',
      label: 'Activity',
      optionsKey: 'activityOptions',
    },
    {
      formControlName: 'location',
      label: 'Location',
      optionsKey: 'locationOptions',
    },
  ];

  private isTimeSelectorDirty = false;
  private initialActivityId: number;

  constructor(
    protected dialogRef: MatDialogRef<DprEditActivityDialogEditComponent>,
    @Inject(MAT_DIALOG_DATA)
    protected data: {
      gridRowData: DprActivityGridData;
      activityGroupOptions: ActivityGroup[];
      customDataList: CustomData[];
      inlineCustomData: CustomData[];
      activityStream;
      activityMobilisations: ActivityMobilisation[];
      canEdit: boolean;
      enableFinishTimeAutoPopulationForActivities: boolean;
      dprDetailId: number | string;
      getActivities: (activityGroupId: number) => Activity[];
      getLocations: (activityId: number) => Location[];
      createActivity: () => Partial<DprActivityGridData>;
      isMilestone: (activityId: number) => boolean;
      getSelectionOptions: (customData: CustomData) => SelectionItem[];
    },
    private syncService: SyncService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private toastNotificationService: ToastNotificationService
  ) {
    super(dialogRef);
    this.form = this.createForm();
    this.initData();

    if (!this.data.canEdit) {
      this.form.disable({ emitEvent: false });
    }
    this.form.statusChanges.subscribe((_) => this.formIsValid());
  }

  protected get customDataFormArray(): FormArray {
    return this.form.get('customDataFormArray') as FormArray;
  }

  protected get activityId(): number {
    return this.form.get('activity')?.value;
  }

  private get isDirty(): boolean {
    return this.form.dirty || this.isTimeSelectorDirty;
  }

  private get baseData(): DprActivityGridData {
    return (
      this.data.gridRowData || {
        id: uuid(),
        activityGroupId: null,
        activityId: null,
        locationId: null,
        start: '',
        finish: '',
        comment: '',
        dprDetailId: this.data.dprDetailId,
        parallelActivityType: this.data.activityStream.parallelActivityType,
        status: null,
        canBeDeleted: true,
        dprSubActivities: [],
        dprActivityCustomDataList: [],
        dprActivityAttachments: [],
        syncDprSubActivities: this.syncService.getDefaultSyncModel(),
        syncDprActivityCustomDataList: this.syncService.getDefaultSyncModel(),
        syncDprActivityAttachments: this.syncService.getDefaultSyncModel(),
      }
    );
  }

  ngOnInit(): void {
    this.updateDependentControls();

    this.form.valueChanges
      .pipe(
        untilDestroyed(this),
        tap(() => this.updateDependentControls())
      )
      .subscribe();

    this.getControl('activityGroup')
      .valueChanges.pipe(
        untilDestroyed(this),
        tap((activityGroupId: number) =>
          this.handleActivityGroupChange(activityGroupId)
        )
      )
      .subscribe();

    this.getControl('activity')
      .valueChanges.pipe(
        untilDestroyed(this),
        tap((activityId: number) => this.handleActivityChange(activityId))
      )
      .subscribe();
  }

  ngAfterViewChecked(): void {
    if (this.hasActivityChanged) {
      const dateTimeFormControls = this.customDataFormArray.controls.filter(
        (formControl) => {
          return (
            formControl.value?.dataType === DataType.Date ||
            formControl.value?.dataType === DataType.Datetime
          );
        }
      );

      let index = 0;

      this.dateTimePickers.forEach((dateTimePicker) => {
        dateTimePicker.closedStream.subscribe((_) => {
          this.cancelDateTime(dateTimePicker);
        });

        const dateTimeFormControl = dateTimeFormControls[index++];
        const computedDateTime: DateTime = this.computeDateTimeFromFormValue(
          dateTimeFormControl?.value
        );

        this.selectedDateTimes.set(dateTimePicker, computedDateTime);
        this.pendingTimes.set(dateTimePicker, computedDateTime.time);
      });

      this.hasActivityChanged = false;
    }
  }

  canDeactivate(): boolean | Observable<boolean> {
    return !this.isDirty;
  }

  save(): Observable<boolean> {
    this.onDone();

    return of(false);
  }

  onDone(): void {
    this.form.markAllAsTouched();
    this.formIsValid();

    if (!this.isFormValid) {
      this.toastNotificationService.warn(
        $localize`:@@dprDetails.dprActivitiesDialogMode.errRequiredFieldsMustBeCompleted:All required fields must be completed.`
      );
      return;
    }

    if (this.startTime > this.finishTime) {
      this.toastNotificationService.warn(this.startBeforeFinishError);
      return;
    }

    const result = this.getUpdatedData(this.baseData);

    if (
      !this.data.gridRowData &&
      !this.autoPopulateFinishTimeIfNeeded(result)
    ) {
      return;
    }
    this.dialogRef.close({ data: result, hasChanges: this.isDirty });
  }

  autoSaveCloseDialog(): { data: DprActivityGridData; hasChanges: boolean } {
    this.form.markAllAsTouched();

    this.dialogRef.close();

    return {
      data: this.getUpdatedData(this.baseData),
      hasChanges: this.isDirty,
    };
  }

  onCancel(): void {
    if (!this.isDirty) {
      this.dialogRef.close();
      return;
    }

    canDeactivateUnsavedChanges(this, this._dialog)
      .pipe(
        untilDestroyed(this),
        filter((val) => val),
        tap(() => {
          this.dialogRef.close();
        })
      )
      .subscribe();
  }

  onStartTimeChanged(time: string): void {
    this.handleTimeChange('startTime', time, () => {
      if (this.isActivityMilestone) {
        this.finishTime = time;
      }
    });

    this.formIsValid();
  }

  onFinishTimeChanged(time: string): void {
    this.handleTimeChange('finishTime', time);

    this.formIsValid();
  }

  onTimeChanged(datetimePicker: MatDatepicker<any>, time: string): void {
    const currentTime = this.pendingTimes.get(datetimePicker);

    if (currentTime === time) {
      return;
    }

    this.pendingTimes.set(datetimePicker, time);
  }

  openDateTimeDialog(datePicker: MatDatepicker<any>): void {
    const currentTime = this.pendingTimes.get(datePicker);
    const dateTime = this.selectedDateTimes.get(datePicker);

    if (!currentTime && !dateTime.isDateOnly) {
      this.pendingTimes.set(datePicker, getCurrentTimeAsString());
    }

    datePicker.open();
  }

  saveDateTime(datePicker: MatDatepicker<any>, index: number): void {
    const dateTime = this.selectedDateTimes.get(datePicker);
    const currentTime = this.pendingTimes.get(datePicker);

    const dateTimeControl = this.customDataFormArray.controls[index];

    dateTime.date =
      dateTimeControl.value?.value ?? new Date(new Date().setHours(0, 0, 0, 0));
    dateTime.time = currentTime;

    if (dateTime.time) {
      dateTime.formattedValue = this.computeFormattedDateTimeValue(dateTime);
      dateTime.date.setHours(
        +dateTime.time.split(':')[0],
        +dateTime.time.split(':')[1]
      );
    }

    datePicker.select(dateTime.date);
  }

  cancelDateTime(datePicker: MatDatepicker<any>): void {
    const dateTime = this.selectedDateTimes.get(datePicker);
    const currentTime = this.pendingTimes.get(datePicker);

    if (dateTime?.time !== currentTime) {
      this.pendingTimes.set(datePicker, dateTime?.time);
    }
  }

  clearDateTime(datePicker: MatDatepicker<any>): void {
    datePicker.select(null);

    const dateTime = this.selectedDateTimes.get(datePicker);
    const currentTime = this.pendingTimes.get(datePicker);

    if (dateTime) {
      dateTime.date = null;

      if (dateTime.time !== undefined) {
        dateTime.time = null;
      }

      if (dateTime.formattedValue !== undefined) {
        dateTime.formattedValue = null;
      }
    }

    if (currentTime) {
      this.pendingTimes.set(datePicker, null);
    }
  }

  formIsValid() {
    this.isFormValid =
      this.form.controls.activityGroup.valid &&
      this.form.controls.activity.valid &&
      this.form.controls.location.valid &&
      this.startTime != null &&
      (this.data.enableFinishTimeAutoPopulationForActivities ||
        this.finishTime != null);
  }

  /**
   * This method is needed because iOS can use comma as a decimal separator depending on locale,
   * but the input should always use dot as the decimal separator for consistency.
   */
  onInputChange(event: Event, index: number): void {
    const value = (event.target as HTMLInputElement).value;

    if (value.includes(',')) {
      this.customDataFormArray
        .at(index)
        .get('value')
        ?.setValue(value.replace(/,/g, '.'));
    }
  }

  private handleTimeChange(
    key: 'startTime' | 'finishTime' | 'selectedTime',
    time: string,
    extra?: () => void,
    markDirty: boolean = true
  ): void {
    if (this[key] === time) {
      return;
    }
    this[key] = time;
    if (extra) {
      extra();
    }
    if (markDirty) {
      this.isTimeSelectorDirty = true;
    }
  }

  private updateFinishTimeIfMilestone(activityId: number): void {
    this.isActivityMilestone = this.data.isMilestone(activityId);

    if (this.isActivityMilestone) {
      this.finishTime = this.startTime;
    }

    this.isTimeSelectorDirty = true;
  }

  private initData(): void {
    this.title = this.data.gridRowData
      ? $localize`:@@dpr.dprActivitiesDialogMode.titleEditActivity:Edit activity`
      : $localize`:@@dpr.dprActivitiesDialogMode.titleAddActivity:Add activity`;

    this.activityGroupOptions = this.data.activityGroupOptions;

    const activityData = this.data.gridRowData
      ? cloneDeep(this.data.gridRowData)
      : this.data.createActivity();

    this.initialActivityId = activityData.activityId;

    this.patchForm(activityData);
  }

  private patchForm(data: Partial<DprActivityGridData>): void {
    this.form.patchValue({
      activityGroup: data.activityGroupId,
      activity: data.activityId,
      location: data.locationId,
      comment: data.comment,
    });

    this.isActivityMilestone = this.data.isMilestone(data.activityId);
    this.startTime = data.start;
    this.finishTime = data.finish;

    this.activityOptions = this.data.getActivities(data.activityGroupId);
    this.locationOptions = this.data.getLocations(data.activityId);

    this.renderCustomData(data.activityId);
    this.patchCustomDataFormArray(data);
  }

  private renderCustomData(activityId: number): void {
    const preservedCustomDataValues = this.customDataFormArray.value;
    this.customDataFormArray.clear(); // Reset form array

    const selectedActivityMobilisations =
      this.data.activityMobilisations.filter(
        (x) =>
          x.activityId === activityId &&
          x.isActive &&
          (x.parallelActivityType ?? 0) ===
            (this.data.activityStream.parallelActivityType ?? 0)
      );

    const customData = this.data.customDataList.filter(
      (x) =>
        selectedActivityMobilisations.some((y) => y.customDataId === x.id) ||
        (activityId === this.initialActivityId &&
          this.data.gridRowData?.dprActivityCustomDataList?.some(
            (y) => y.customDataId === x.id
          ))
    );

    customData.forEach((cd) => {
      const customDataSelectionOptions = this.data.getSelectionOptions(cd);

      const numericInputConfig: NumericInputOptions =
        cd.dataTypeId === DataType.Number
          ? {
              min: 0,
              max: Constants.NumericOptions.maxActivitiesCustomData,
              step: 1 / Math.pow(10, cd.decimalCount ?? 2),
              decimalPrecision: cd.decimalCount ?? 2,
              decimalSeparator: [',', '.'],
            }
          : cd.dataTypeId === DataType.Percentage
          ? {
              min: 0,
              max: Constants.NumericOptions.maxPercentage,
              step: 0.01,
              decimalPrecision: 2,
              allowPercentage: true,
              allowSign: false,
              decimalSeparator: [',', '.'],
            }
          : {};

      const oldValue = preservedCustomDataValues.find(
        (v) => v.customDataId === cd.id
      )?.value;

      this.customDataFormArray.push(
        this.fb.group({
          id: uuid(),
          name: [cd.name],
          customDataId: [cd.id],
          dataType: [cd.dataTypeId],
          decimalCount: [cd.decimalCount],
          remark: [cd.remark],
          priority: [cd.priority],
          booleanTerminologyId: [cd.booleanTerminologyId],
          value: [{ value: oldValue, disabled: !this.data.canEdit }],
          comment: [{ value: null, disabled: !this.data.canEdit }],
          selectOptions: [customDataSelectionOptions || []],
          numericInputConfig: numericInputConfig,
          isMandatory: [
            selectedActivityMobilisations?.find((x) => x.customDataId === cd.id)
              ?.isMandatory || false,
          ],
        })
      );
    });
  }

  private patchCustomDataFormArray(data: Partial<DprActivityGridData>): void {
    data.dprActivityCustomDataList?.forEach((cd) => {
      const control = this.customDataFormArray.controls.find(
        (ctrl) => ctrl.get('customDataId')?.value === cd.customDataId
      );
      control?.patchValue({
        id: cd.id,
        value: cd.value,
      });
    });
  }

  private getUpdatedData(editedData: DprActivityGridData): DprActivityGridData {
    const data: DprActivityGridData = {
      ...(editedData ? cloneDeep(editedData) : {}),
      activityGroupId: this.form.get('activityGroup')?.value,
      activityId: this.form.get('activity')?.value,
      locationId: this.form.get('location')?.value,
      comment: this.form.get('comment')?.value,
      start: this.startTime,
      finish: this.finishTime,
      syncDprActivityCustomDataList:
        editedData.syncDprActivityCustomDataList ??
        this.syncService.getDefaultSyncModel(),
    };

    const customDataFormArray = this.customDataFormArray.controls.map(
      (field) => ({
        id: field.get('id')?.value,
        customDataId: field.get('customDataId')?.value,
        value: field.get('value')?.value,
        dirty: field.dirty,
        ...field.value,
      })
    );

    const addOrUpdateCustomData = (field: any) => {
      //if no modification was made, skip the custom data operations
      if (!field.dirty) {
        return;
      }

      const customDataFieldValue = this.getCustomDataValue(
        field.value,
        field.dataType
      );

      const isDeleted =
        field.dirty && String.isNullOrWhiteSpace(customDataFieldValue);
      const existingCustomData = data.dprActivityCustomDataList?.find(
        (cd) => cd.id === field.id && cd.customDataId === field.customDataId
      );

      // if the custom data already exists on the grid, update it accordingly
      if (existingCustomData) {
        existingCustomData.value = customDataFieldValue;

        const addedCustomData =
          data?.syncDprActivityCustomDataList?.addedList.find(
            (x) => x.id === existingCustomData.id
          );

        // if the custom data exists and is deleted, check if it exists in the added list
        // if it does, remove it from the added list
        // if it does not, add it to the deleted list
        if (isDeleted) {
          if (addedCustomData) {
            data.syncDprActivityCustomDataList.addedList =
              data?.syncDprActivityCustomDataList?.addedList.filter(
                (x) => x.id !== addedCustomData.id
              );
          } else {
            // if the custom data was not already marked as deleted
            if (
              !data.syncDprActivityCustomDataList.deletedIds.find(
                (x) => x === field.id
              )
            ) {
              data.syncDprActivityCustomDataList.deletedIds.push(field.id);
            }
          }

          return;
        }

        // if the custom data was deleted during a previous operation, clear it from the removed list
        if (!isDeleted) {
          data.syncDprActivityCustomDataList.deletedIds =
            data.syncDprActivityCustomDataList.deletedIds.filter(
              (x) => x !== field.id
            );
        }

        //if the custom data was added to the grid but is not persistent, update the value of the added custom data
        // in the added list
        if (addedCustomData) {
          addedCustomData.value = customDataFieldValue;
        } else {
          // if the custom data is persistent, update it in the updated list
          const updatedCustomData =
            data.syncDprActivityCustomDataList.updatedList.find(
              (x) => x.id === existingCustomData.id
            );
          if (updatedCustomData) {
            updatedCustomData.value = customDataFieldValue;
            return;
          }

          data.syncDprActivityCustomDataList.updatedList.push(
            existingCustomData
          );
        }

        return;
      }

      //if the custom data is new data but it is empty, skip the add operations
      if (isDeleted) {
        return;
      }

      // if the custom data is new, create a new object and add it to the list
      const newCustomData: DprActivityCustomDataGridData = {
        ...field,
        dprActivityId: data.id,
        comment: '',
        value:
          field.dataType === DataType.Date && field.value
            ? convertToDateTimeWithoutTimeZoneSuffix(field.value)
            : field.dataType === DataType.Datetime && field.value
            ? convertToDateTimeWithoutTimeZoneSuffix(
                field.value,
                field.value.getHours(),
                field.value.getMinutes()
              )
            : field.value,
      };

      data.dprActivityCustomDataList.push(newCustomData);
      data.syncDprActivityCustomDataList.addedList.push(newCustomData);
    };

    customDataFormArray.forEach(addOrUpdateCustomData);

    // clear old activity custom data that are no longer associated with current activity
    const obsoleteCustomDataIds =
      this.data.gridRowData?.dprActivityCustomDataList
        ?.filter(
          (cd) =>
            !customDataFormArray.some(
              (field) => field.customDataId === cd.customDataId
            )
        )
        .map((dprActivityCustomData) => dprActivityCustomData.id) ?? [];

    data.syncDprActivityCustomDataList.deletedIds.push(
      ...obsoleteCustomDataIds
    );
    data.dprActivityCustomDataList = data.dprActivityCustomDataList.filter(
      (x) => !obsoleteCustomDataIds.includes(x.id)
    );

    this.data.inlineCustomData.forEach((cd) => {
      const customField = customDataFormArray?.find(
        (field) => field.customDataId === cd.id
      );

      if (customField) {
        data[cd.id] = customField.value;
      }
    });

    return data;
  }

  private autoPopulateFinishTimeIfNeeded(
    rowData: DprActivityGridData
  ): boolean {
    if (
      !this.data.enableFinishTimeAutoPopulationForActivities ||
      !rowData?.start ||
      rowData?.finish
    ) {
      return true; // No auto-population needed
    }

    const currentTime = getCurrentTimeAsString();
    const startTime = convertTimeToDate(rowData.start);

    // Start time is valid, auto-populate finish time
    rowData.finish = currentTime;
    this.finishTime = currentTime;
    this.isTimeSelectorDirty = true;

    if (compareTime(startTime, convertTimeToDate(currentTime)) > 0) {
      this.toastNotificationService.warn(this.startBeforeFinishError);
      return false; // Prevent dialog closure
    }

    return true;
  }

  private createForm(): FormGroup {
    return this.fb.group({
      activityGroup: [
        { value: null, disabled: !this.data.canEdit },
        Validators.required,
      ],
      activity: [
        { value: null, disabled: !this.data.canEdit },
        [Validators.required],
      ],
      location: [
        { value: null, disabled: !this.data.canEdit },
        [Validators.required],
      ],
      comment: [{ value: null, disabled: !this.data.canEdit }],
      customDataFormArray: this.fb.array([]),
    });
  }

  private updateDependentControls(): void {
    if (!this.data.canEdit) {
      return;
    }

    this.setControlState(
      this.getControl('activity'),
      !!this.getControl('activityGroup')?.value
    );
    this.setControlState(
      this.getControl('location'),
      !!this.getControl('activity')?.value
    );
  }

  private setControlState(control: AbstractControl, enabled: boolean): void {
    enabled
      ? control.enable({ emitEvent: false })
      : control.disable({ emitEvent: false });
  }

  private getControl(name: string): AbstractControl {
    return this.form.get(name) as AbstractControl;
  }

  private computeFormattedDateTimeValue(dateTime: DateTime): string {
    const datePart = convertDateToDisplayFormat(dateTime.date);
    const timeString = dateTime.time?.match(/^(\d{2}:\d{2}:\d{2})/)[1];
    const timePart = convertTimeToDisplayFormat(timeString);

    return `${datePart} ${timePart}`;
  }

  private computeDateTimeFromFormValue(value: any): DateTime {
    if (!value.value) {
      return {
        date: null,
        time: null,
        formattedValue: null,
        isDateOnly: value.dataType === DataType.Date ? true : false,
      };
    }

    let date: Date = null;
    let time: string = null;
    let isDateOnly: boolean = true;
    const dateClone = new Date(value.value);
    const dateString = convertToDateTimeWithoutTimeZoneSuffix(
      dateClone,
      dateClone.getHours(),
      dateClone.getMinutes()
    );

    switch (value.dataType) {
      case DataType.Date:
        date = new Date(dateString);
        time = null;
        isDateOnly = true;
        break;
      case DataType.Datetime:
        date = new Date(dateString.split('T')[0]);
        time = dateString.split('T')[1];
        isDateOnly = false;
        break;
      default:
        break;
    }

    const computedDateTime: DateTime = {
      date: date,
      time: time,
      isDateOnly: isDateOnly,
    };

    computedDateTime.formattedValue = isDateOnly
      ? null
      : this.computeFormattedDateTimeValue(computedDateTime);

    return computedDateTime;
  }

  private handleActivityGroupChange(activityGroupId: number): void {
    this.activityOptions = this.data.getActivities(activityGroupId);
    this.cdr.detectChanges(); // Ensure the view is updated before patching the value

    const shouldAutoSelectActivity = this.activityOptions.length === 1;
    const activityId = shouldAutoSelectActivity
      ? +this.activityOptions[0].id
      : null;

    this.form.patchValue({
      activity: activityId,
      location: null,
    });

    if (activityId) {
      this.updateFinishTimeIfMilestone(activityId);
      this.renderCustomData(activityId);
      this.resetCustomDataValues(activityId);
    }
  }

  private handleActivityChange(activityId: number): void {
    this.locationOptions = this.data.getLocations(activityId);
    this.cdr.detectChanges(); // Ensure the view is updated before patching the value

    const currentLocationId = this.form.get('location')?.value;
    const locationId = this.selectLocationId(currentLocationId);

    this.form.patchValue({ location: locationId });

    if (activityId) {
      this.updateFinishTimeIfMilestone(activityId);
      this.renderCustomData(activityId);
      this.resetCustomDataValues(activityId);
    }

    this.hasActivityChanged = true;
  }
  private resetCustomDataValues(activityId: number): void {
    if (this.initialActivityId && activityId === this.initialActivityId) {
      this.patchCustomDataFormArray(this.data.gridRowData);
    }
  }

  private getCustomDataValue(value: string | Date, dataType: DataType): string {
    if (value == null) {
      return null;
    }

    if (
      (dataType !== DataType.Date && dataType !== DataType.Datetime) ||
      typeof value === 'string'
    ) {
      return value as string;
    }

    const hours = dataType === DataType.Datetime ? value?.getHours() : null;
    const min = dataType === DataType.Datetime ? value?.getMinutes() : null;

    return convertToDateTimeWithoutTimeZoneSuffix(value, hours, min);
  }

  private selectLocationId(currentLocationId: number): number | null {
    // Auto-select if only one option available
    if (this.locationOptions.length === 1) {
      return +this.locationOptions[0].id;
    }
    // Keep current selection if it's still valid
    return this.locationOptions.some(
      (location) => location.id === currentLocationId
    )
      ? currentLocationId
      : null;
  }

  private get startBeforeFinishError(): string {
    return $localize`:@@dprDetails.dprActivitiesDialogMode.errStartMustBeBeforeFinish:Start must be before Finish.`;
  }
}
